import { createElementsFromHtml } from '@Support/create-elements-from-html';
import { getAllDataset } from '@Support/get-all-dataset';
import { isVisible } from '@Support/element/is-visible';
import { noop } from '@Support/noop';
import throttle from 'lodash.throttle';

const EVENTS_NAMESPACE = 'icube.lazyhtml.';

function updateUrl(url) {
    if (window.history.pushState && url !== window.location.href) {
        window.history.pushState({}, "", url);
    }
}

function render($el, data, config) {
    if (data.remove) {
        $el.remove();
    } else {
        config.beforeRender($el);

        if (config.keepDimensions && ($el.offsetWidth > 0 && $el.offsetHeight > 0)) {
            $el.style.setProperty('min-width', $el.offsetWidth);
            $el.style.setProperty('min-height', $el.offsetHeight);

            if (!$el.dataset.lhDimensionsSaved) {
                window.addEventListener('resize', function() {
                    $el.style.removeProperty('min-width');
                    $el.style.removeProperty('min-height');
                    $el.dataset.lhDimensionsSaved = false;
                }, { once: true });

                $el.dataset.lhDimensionsSaved = true;
            }
        }

        if (data.html || (!data.html && config.allowEmptyHtml)) {
            const children = [...$el.children];
            for (let i = 0, len = children.length; i < len; i++) {
                const $child = children[i];

                if ($child.classList.contains(config.exceptionClass)) {
                    continue;
                }

                $child.remove();
            }

            const newElements = [...createElementsFromHtml(data.html)];
            for (let i = 0, len = newElements.length; i < len; i++) {
                $el.append(newElements[i]);
            }

            config.afterRender($el);
        }
    }
}

function getHeadersByMethod(method) {
    const headers = {
        'X-Requested-With': 'XMLHttpRequest'
    };

    switch (method) {
        case 'POST':
        case 'PUT':
            headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
            break;

        default: /* GET */
            headers['Accept'] = 'application/json, text/javascript, */*';
    }

    return headers;
}

function validRequestParams(params) {
    return params.src !== '' && ['GET','POST','PUT'].indexOf(params.method) > -1;
}

function sendRequest(params) {
    return new Promise(function(resolve, reject) {
        if (validRequestParams(params)) {
            const options = {
                method: params.method,
                headers: getHeadersByMethod(params.method),
            };

            if (params.body) {
                options.body = JSON.stringify(params.body)
            }

            fetch(params.src, options)
                .then((response) => {
                    if (!response.ok) {
                        reject('@Modules/lazy-html: request failed.');
                    }
                    return response.json();
                })
                .then((data) => {
                    if (data.hasOwnProperty('html')) {
                        resolve(data);
                    } else {
                        reject('@Modules/lazy-html: invalid response data (html does not exist).');
                    }
                }).catch((error) => {
                    reject(`@Modules/lazy-html: ${error}`);
                });
        } else {
            reject('@Modules/lazy-html: invalid request parameters.');
        }
    });
}

function eventHandler($el, config) {
    if (isVisible($el)) {
        config.beforeRequest($el);

        sendRequest(config)
            .then(function(data) {
                render($el, data, config);

                if (config.url) {
                    updateUrl(data.url);
                }
            })
            .catch(function(errorMessage) {
                console.warn(errorMessage);
            });
    }
}

function initRequest($el, config) {
    config.beforeRequest($el);

    sendRequest(config)
        .then(function(data) {
            render($el, data, config);

            if (config.refresh > 0) {
                interval = setInterval(function() {
                    config.beforeRequest($el);

                    sendRequest(config)
                        .then(function(data) {
                            render($el, data, config);

                            if (data.remove) {
                                clearInterval(interval);
                            }
                        })
                        .catch(function(errorMessage) {
                            clearInterval(interval);
                            console.warn(errorMessage);
                        });
                }, config.refresh);
            }
        })
        .catch(function(errorMessage) {
            console.warn(errorMessage);
        });
}

function initTrigger($el, config) {
    const $trigger = typeof config.trigger === 'string'
        ? document.querySelector(config.trigger)
        : config.trigger;

    $trigger.addEventListener(config.triggerevent, throttle(function() {
        eventHandler($el, config);
    }, 500), { once: config.singlerequest });
}

function addCustomEventForRefresh($el, config) {
    $el.addEventListener(EVENTS_NAMESPACE + 'refresh', throttle(function(event) {
        const newConfig = event.detail || null;
        const configOnRefresh = Object.assign({}, config, newConfig);
        eventHandler($el, configOnRefresh);
    }, 500));
}

export default function($elements, initialConfig) {
    const defaultConfig = {
        src: '',
        method: 'GET',
        body: null,
        refresh: 0,
        keepDimensions: true,
        exceptionClass: '',
        trigger: '',
        triggerevent: 'click',
        singlerequest: false,
        url: false,
        allowEmptyHtml: true,
        onInit: noop,
        beforeRequest: noop,
        beforeRender: noop,
        afterRender: noop
    };

    if (initialConfig) {
        Object.assign(defaultConfig, initialConfig);
    }

    for (const $el of $elements) {
        const config = Object.assign({}, defaultConfig, getAllDataset($el));

        config.onInit($el);
        addCustomEventForRefresh($el, config);

        if (config.trigger) {
            initTrigger($el, config);
        } else {
            initRequest($el, config);
        }
    }
}